import React from 'react'
import * as R from 'remeda'

import { useIsLoadingCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useRouteParams } from '~/hooks/useRouteParams'
import { exists, findByPath } from '~/utils'

import { useCurrentBrandAccountSurveysQuery } from './useCurrentBrandAccountSurveysQuery'
import { type Survey } from './useCurrentBrandAccountSurveysQuery'

type CurrentSurveyResult =
  | {
      success: false
      reason: 'loading' | 'notDetermined' | 'notExist'
      value: undefined
    }
  | {
      success: true
      value: NonNullable<Survey>
    }

export const useCurrentSurvey = (): CurrentSurveyResult => {
  const { brandAccountId, surveyId } = useRouteParams()

  const { data, isError, isLoading } = useCurrentBrandAccountSurveysQuery()
  const isLoadingCurrentBrandAccount =
    useIsLoadingCommonQueryCurrentBrandAccount()

  return React.useMemo(() => {
    // pathからbrandAccountIdとsurveyIdの両方が取得できない場合、判定できないので諦める
    if (!exists(brandAccountId) && !exists(surveyId)) {
      return { success: false, reason: 'notDetermined' }
    }
    // currentBrandAccountの解決に必要なクエリ群の取得中もローディングとして扱う
    // (解決前はクエリがdisabledでisLoadingにならないため)
    if (isLoading || isLoadingCurrentBrandAccount) {
      return { success: false, reason: 'loading' }
    }
    // 依存クエリが全て確定してもデータが得られない場合(クエリのエラーや、
    // currentBrandAccountが解決できずクエリがdisabledのままの場合)は、
    // ローディング扱いにすると永遠に終わらないので存在しない扱いにする
    // (TanStack Queryのdataの型はNoInfer<TData>で包まれておりexists()では
    //  絞り込めないため、undefinedとの直接比較で判定する)
    if (isError || data === undefined) {
      return { success: false, reason: 'notExist' }
    }
    if (exists(brandAccountId) || !exists(surveyId)) {
      return { success: false, reason: 'notExist' }
    }

    const value = R.pipe(data, findByPath('id', surveyId))
    if (exists(value)) {
      return { success: true, value }
    } else {
      return { success: false, reason: 'notExist' }
    }
  }, [
    brandAccountId,
    data,
    isError,
    isLoading,
    isLoadingCurrentBrandAccount,
    surveyId,
  ])
}
